import { AsyncPipe, DatePipe, SlicePipe } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { Router } from "@angular/router";
import { ActionModel } from "@models/alerts/action-model";
import { Alert } from "@models/alerts/alert";
import { ActionType, AlertStatus, AlertType, ResolvedStatus } from "@models/alerts/alerts-enums";
import { EntityType } from "@models/common/entity-type.enum";
import { CompanyDebtExtended } from "@models/companies/company-debt-extended";
import { ColumnWithExportName } from "@models/grid/column-with-export-name";
import { FileData, FileMetadata } from "@models/ui-models/files";
import {
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbTooltip
} from "@ng-bootstrap/ng-bootstrap";
import { postponeMenu } from "@pages/alerts/constants/postpone-options";
import { AlertHelper } from "@pages/alerts/utils/alert-helper";
import { getPostponeDate } from "@pages/alerts/utils/get-postpone-date";
import { ITagsEntity } from "@pages/settings/components/manage-tags-page/manage-tags-modal/manage-tags-modal.constant";
import { FileLabelComponent } from "@shared/components/commons/file-label/file-label.component";
import {
  AgAlertsResolvedStatusComponent
} from "@shared/components/grid/ag-cells/alerts/ag-alerts-resolved-status/ag-alerts-resolved-status.component";
import {
  AgAlertsStatusComponent
} from "@shared/components/grid/ag-cells/alerts/ag-alerts-status/ag-alerts-status.component";
import {
  AgCompanyNameAcnComponent
} from "@shared/components/grid/ag-cells/common/ag-company-name-acn/ag-company-name-acn.component";
import {
  AgTagEntityTitleComponent
} from "@shared/components/grid/ag-cells/common/ag-tag-entity-title/ag-tag-entity-title.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { HasRoleDirective } from "@shared/directives/has-role.directive";
import { InsertLineBreakPipe } from "@shared/pipes/insert-line-break.pipe";
import { AlertsService } from "@shared/services/alert.service";
import { AlertsApiService } from "@shared/services/alerts-api.service";
import { EntityIdentityApiService } from "@shared/services/entity-identity-api.service";
import { downloadBase64File } from "@shared/utils/download-base64-file";
import { getAnnualStatementsPath, getGenerateDocumentPath } from "@shared/utils/route-path-utils";
import { observe, Observer } from "fast-json-patch";
import { ToastrService } from "ngx-toastr";
import { catchError, Observable, of, tap, throwError } from "rxjs";
import { ModalFormsService } from "../../../../forms/services/modal-forms.service";

@Component({
  selector: 'app-alert-details-content',
  standalone: true,
  imports: [
    DatePipe,
    SlicePipe,
    InsertLineBreakPipe,
    HasRoleDirective,
    FileLabelComponent,
    AgAlertsResolvedStatusComponent,
    AgAlertsStatusComponent,
    AgTagEntityTitleComponent,
    ListGridClientSideComponent,
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    NgbTooltip,
  ],
  templateUrl: 'alert-details-content.component.html',
  styleUrl: 'alert-details-content.component.scss'
})
export class AlertDetailsContentComponent {
  alertsService = inject(AlertsService);
  alertsApiService = inject(AlertsApiService);
  entityIdentityApiService = inject(EntityIdentityApiService);
  modalFormsService = inject(ModalFormsService);
  router = inject(Router);
  destroyRef = inject(DestroyRef);
  toastr = inject(ToastrService);

  headerHeight = input('0px');
  alertSetter = input<Alert | null>(null);

  updateAlert = output<Alert>();
  readStatusChange = output<string>();
  close = output<void>();

  readonly AlertStatus = AlertStatus;
  readonly AlertType = AlertType;
  readonly ResolvedStatus = ResolvedStatus;
  readonly postponeMenu = postponeMenu;

  attachments: FileMetadata[] | null = null;
  alert: Alert | null = null;

  observer!: Observer<Alert>;
  entityType: EntityType | null = null;
  entity: ITagsEntity | undefined;
  isPostponeVisible = false;
  isArchiveActionVisible = false;
  alertActions: ActionModel[] = [];
  companiesDebtDetails: { isCompanyDebtReportAsicAlertDetail: boolean, companiesDebt: CompanyDebtExtended[] } = {
    isCompanyDebtReportAsicAlertDetail: false,
    companiesDebt: []
  };

  isAlertLoading = signal(false);
  isAttachmentsLoading = signal(false);
  isActionsDisabled = signal(false);

  constructor() {
    effect(() => {
      const alert = this.alertSetter();

      if (alert) this.init(alert);
    });
  }

  init(alert: Alert): void {
    this.alert = alert;
    this.observer = observe(this.alert);
    this.entityType = this.alert.entityType;
    this.entity = this.alert.entity as unknown as ITagsEntity | undefined;
    this.isPostponeVisible = AlertHelper.isPostponeVisible(this.alert);
    this.isArchiveActionVisible = AlertHelper.isArchiveVisible(this.alert);


    this.loadAttachments(this.alert.id);
    this.fillAlertActions();
    this.fillCompanyDebtDetails();
    this.setReadStatus();
  }

  loadAttachments(alertId: string): void {
    this.isAttachmentsLoading.set(true);

    this.alertsApiService.getAttachments(alertId)
      .subscribe({
        next: (attachments) => {
          this.attachments = attachments;
          this.isAttachmentsLoading.set(false);
        },
        error: (error) => {
          console.warn('[loadAttachments] error: ', error);
          this.toastr.error('Failed to load alert attachments', 'Error');
          this.isAttachmentsLoading.set(false);
        }
      });
  }

  fillAlertActions(): void {
    if (!this.alert?.alertDetail || !this.alert?.alertDetail?.actions?.length) {
      this.alertActions = [];
      return;
    }

    this.alertActions = this.alert.alertDetail.actions
      .filter((action) => !(action.actionType == ActionType.OpenDocument && !action.actionData))
      .map((action) => new ActionModel(action));
  }

  fillCompanyDebtDetails(): void {
    const alert = this.alert as Alert;

    this.companiesDebtDetails.isCompanyDebtReportAsicAlertDetail = alert.alertDetail.$type === 'CompanyDebtReportAsiAlertDetail';

    if (this.companiesDebtDetails.isCompanyDebtReportAsicAlertDetail) {
      this.companiesDebtDetails.companiesDebt = ((alert.alertDetail as any).companiesDebt as CompanyDebtExtended[] ?? []).map((debt) => new CompanyDebtExtended(debt));
    } else {
      this.companiesDebtDetails.companiesDebt = [];
    }
  }

  setReadStatus(): void {
    if (this.alert?.status === AlertStatus.New && !this.alert.isRead) {
      this.alert.isRead = true;

      this.patchAlertData()
        .subscribe({
          next: () => {
            this.readStatusChange.emit(this.alert!.id);
            this.alertsService.notifyReadStatusUpdated();
          },
          error: (error) => {
            console.warn('[setReadStatus] error: ', error);
          }
        });
    }
  }

  handleAlertAction(alert: Alert, action: ActionModel): void {
    if (this.preprocessAlert(alert, action)) {
      return;
    }

    this.isActionsDisabled.set(true);

    this.alertsApiService.resolve(alert.id, action.id)
      .subscribe({
        next: (updatedAlert) => {
          this.alert = updatedAlert;
          this.toastr.success('Successfully resolved alert', 'Success');
          this.postprocessAlert(alert, action);
          this.updateAlert.emit(updatedAlert);
          this.isActionsDisabled.set(false);
        },
        error: (error) => {
          if (this.entityIdentityApiService.checkSyncError(error)) {
            this.isActionsDisabled.set(false);
          }

          this.toastr.error('Failed to resolve alert');
        }
      });
  }

  preprocessAlert(alert: Alert, action: ActionModel): boolean {
    switch (action.actionType) {
      case ActionType.PrepareForm492:
        void this.router.navigate([`/companies/${ alert.entityId }`], {
          queryParams: { openForm: 492 },
        });
        return true;
      case ActionType.Manual:
        return this.processManualAction(alert, action);
      case ActionType.OpenDocument:
        return this.processOpenDocumentAction(alert, action);
      default:
        return false;
    }
  }

  processManualAction(alert: Alert, action: ActionModel): boolean {
    switch (alert.alertDetail.$type) {
      case 'SecurityIssueAsicAlertDetail': {
        void this.router.navigate([`/shares/${ action.actionData }`]);
        return true;
      }
      case 'InvoiceAsicAlertDetail': {
        void this.router.navigate([`asic-invoices/${ action.actionData }`]);
        return true;
      }
      case 'BillingContactMissingAlertDetail': {
        void this.router.navigate([`/companies/${ action.actionData }`], {
          queryParams: { openModal: 'openBillingModal' },
        });
        return true;
      }
      default:
        return false;
    }
  }

  processOpenDocumentAction(alert: Alert, action: ActionModel): boolean {
    switch (alert.alertDetail.$type) {
      case 'ValidationReportAsicAlert':
      case 'ChangeOfNameCertificateAsicAlert': {
        void this.router.navigate([`documents/${ action.actionData }`]);
        return true;
      }
      case 'BillingContactMissingAlertDetail': {
        void this.router.navigate(getAnnualStatementsPath(action.actionData));
        return true;
      }
      default:
        return false;
    }
  }

  postprocessAlert(alert: Alert, action: ActionModel): void {
    switch (action.actionType) {
      case ActionType.AsicForm:
        void this.router.navigate(getGenerateDocumentPath(action.actionData));
        return;
      case ActionType.Manual:
        if (this.alert!.alertDetail.$type == 'AnnualStatementAlertDetail') {
          void this.router.navigate(getAnnualStatementsPath(action.actionData));
          return;
        }
    }
  }

  patchAlertData(): Observable<any> {
    this.isActionsDisabled.set(true);

    return this.alertsApiService.updateAlert(this.alert!.id, this.observer)
      .pipe(
        tap((result) => {
          this.alert = result;
          this.observer = observe(this.alert);

          this.updateAlert.emit(result);
          this.isActionsDisabled.set(false);
        }),
        catchError((error) => {
          console.error('[patchAlertData] error: ', error);
          this.toastr.error("Failed to update alert", "Error");
          this.isActionsDisabled.set(false);
          return throwError(() => error);
        })
      );
  }

  downloadFile(alertId: string, fileName: string): void {
    this.alertsApiService.getDownload(alertId, fileName)
      .subscribe({
        next: (res) => {
          if (res) {
            downloadBase64File(res, fileName);
            this.toastr.success("Documents have been successfully downloaded", "Success");
          }
        },
        error: (error: HttpErrorResponse) => {
          console.error('[downloadFile] error: ', error);
          this.toastr.error("Download Documents error", "Error");
        }
      });
  }

  downloadAllAttachments(): void {
    this.alertsApiService.downloadAllAttachments(this.alert!.id)
      .subscribe({
        next: (res: FileData) => {
          if (res) {
            downloadBase64File(res.fileByte, res.fileName);
            this.toastr.success("Attachments have been successfully downloaded", "Success");
          }
        },
        error: (error) => {
          console.warn('[downloadAllAttachments] error: ', error);
          this.toastr.error("Download Documents error", "Error");
        }
      });
  }

  postponeAlert(numberOfDays: number): void {
    if (this.isActionsDisabled() || !this.alert) return;

    this.alert.postponedTill = getPostponeDate(numberOfDays);
    this.alert.status = AlertStatus.Postponed;

    this.patchAlertData()
      .subscribe({
        next: () => {
          this.toastr.success(`Selected alert postponed`, 'Success');
        },
        error: (error) => {
          console.warn('[postponeAlert] error: ', error);
        }
      });
  }

  archiveAlert(): void {
    this.alert!.status = AlertStatus.Archived;

    this.patchAlertData()
      .subscribe({
        next: () => {
          this.toastr.success(`Selected alert archived`, 'Success');
        },
        error: (error) => {
          console.warn('[archiveAlert] error: ', error);
        }
      });
  }

  colDefs: ColumnWithExportName[] = [
    {
      headerName: 'Company Name & ACN',
      exportColumnName: 'Company Name',
      suppressMovable: true,
      field: 'name',
      minWidth: 240,
      flex: 2.4,
      editable: false,
      sort: 'asc',
      cellRenderer: AgCompanyNameAcnComponent,
      menuTabs: ['generalMenuTab'],
    },
    {
      headerName: 'Total Amount Owing',
      field: 'amountOwing',
      minWidth: 170,
      flex: 1,
    },
    {
      headerName: 'Future Debt',
      field: 'futureDue',
      minWidth: 120,
      flex: 1,
    },
    {
      headerName: 'Debt Now Due',
      field: 'nowDue',
      minWidth: 140,
      flex: 1,
    },
    {
      headerName: 'Overdue (0-30 days)',
      field: 'overdue0_30',
      minWidth: 180,
      flex: 1,
    },
    {
      headerName: 'Overdue (31-60 days)',
      field: 'overdue31_60',
      minWidth: 180,
      flex: 1,
    },
    {
      headerName: 'Overdue (61-90 days)',
      field: 'overdue61_90',
      minWidth: 180,
      flex: 1,
    },
    {
      headerName: 'Overdue (90+ days)',
      field: 'overdue90plus',
      minWidth: 180,
      flex: 1,
    }
  ];
}
